import React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const Filter = ({
  variant = "drawer",
  open,
  onClose,
  priceRange,
  setPriceRange,
  computedMinPrice = 0,
  computedMaxPrice = 5000,
  selectedBrands,
  setSelectedBrands,
  selectedIdealFor,
  setSelectedIdealFor,
  selectedUnitType = [],
  setSelectedUnitType = () => {},
  codAvailable = "all",
  setCodAvailable = () => {},
  brands: propsBrands = [],
  idealForOptions: propsIdealFor = [],
  skinTypeOptions: propsSkinTypes = [],
  hairTypeOptions: propsHairTypes = [],
  hasSomeVegan = false,
  hasSomeCrueltyFree = false,
  hasSomeOrganic = false,
  hasSomeCOD = false,
  selectedSkinTypes = [],
  setSelectedSkinTypes = () => {},
  selectedHairTypes = [],
  setSelectedHairTypes = () => {},
  isVegan = false,
  setIsVegan = () => {},
  isCrueltyFree = false,
  setIsCrueltyFree = () => {},
  isOrganic = false,
  setIsOrganic = () => {},
  includeOutOfStock = false,
  setIncludeOutOfStock = () => {},
  onClearFilters,
  onApply,
}) => {
  const brands = propsBrands;
  const idealForOptions = propsIdealFor;
  const skinTypes = propsSkinTypes;
  const hairTypes = propsHairTypes;
  const showTrustBadges = hasSomeVegan || hasSomeCrueltyFree || hasSomeOrganic;

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.some(b => b.toLowerCase() === brand.toLowerCase())
        ? prev.filter((b) => b.toLowerCase() !== brand.toLowerCase())
        : [...prev, brand]
    );
  };

  const handleSkinTypeChange = (type) => {
    setSelectedSkinTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleHairTypeChange = (type) => {
    setSelectedHairTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const sectionBox = {
    mb: 3,
    p: 2.5,
    borderRadius: 2,
    backgroundColor: variant === "static" ? "#fff" : "background.default",
    border: variant === "static" ? "1px solid #f0f0f0" : "none",
    boxShadow: variant === "static" ? "0 2px 8px rgba(0,0,0,0.02)" : "none",
  };

  const labelStyle = {
    ...rubikfont,
    fontWeight: 500,
    mb: 1.5,
    display: "block",
    fontSize: "0.95rem",
    color: "#222",
  };

  const checkboxSx = {
    color: "action.active",
    "&.Mui-checked": { color: "#C8A054" },
    padding: "6px",
  };

  const radioSx = {
    color: "action.active",
    "&.Mui-checked": { color: "#C8A054" },
    padding: "6px",
  };

  const FilterContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header — Drawer variant only */}
      {variant === "drawer" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 2 }}>
            <Typography variant="h5" sx={{ ...rubikfont, fontWeight: 500, color: "text.primary" }}>
              Filters
            </Typography>
            <Typography 
              onClick={onClearFilters} 
              sx={{ fontSize: "13px", color: "#C8A054", cursor: "pointer", textDecoration: "underline", fontWeight: 600, ...rubikfont }}
            >
              Reset All
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ backgroundColor: "action.hover", "&:hover": { backgroundColor: "action.selected" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Static Header */}
      {variant === "static" && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ ...rubikfont, fontWeight: 500 }}>
              Filter Results
            </Typography>
            <Typography 
              onClick={onClearFilters} 
              sx={{ fontSize: "13px", color: "#C8A054", cursor: "pointer", textDecoration: "underline", fontWeight: 600, ...rubikfont }}
            >
              Reset All
            </Typography>
          </Box>
          <Divider sx={{ width: "40px", height: "3px", bgcolor: "#C8A054", border: "none" }} />
        </Box>
      )}

      {/* Scrollable Content */}
      <Box
        sx={{
          p: variant === "static" ? 0 : 3,
          overflowY: variant === "static" ? "visible" : "auto",
          flexGrow: 1,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "divider", borderRadius: 2 },
        }}
      >
        {/* ── Price Range — always shown ── */}
        <Box sx={sectionBox}>
          <FormLabel component="legend" sx={{ ...labelStyle, mb: 2 }}>
            Price Range
          </FormLabel>
          <Slider
            value={priceRange}
            onChange={handlePriceChange}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `₹${value}`}
            min={computedMinPrice}
            max={computedMaxPrice}
            sx={{
              mb: 2,
              mt: 4,
              color: "#C8A054",
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
                backgroundColor: "#fff",
                border: "2px solid currentColor",
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: "#C8A054",
                color: "#fff",
                fontWeight: 600,
                fontSize: "12px",
                borderRadius: "4px",
                padding: "2px 6px",
              }
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#999" }}>Min: ₹{computedMinPrice}</Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#999" }}>Max: ₹{computedMaxPrice}</Typography>
          </Box>
        </Box>

        {/* ── Brand — only rendered when brands exist ── */}
        {brands.length > 0 && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>Brand</FormLabel>
            <FormGroup sx={{ gap: 0, maxHeight: 300, overflowY: "auto", pr: 1 }}>
              {brands.map((brand) => (
                <FormControlLabel
                  key={brand}
                  control={
                    <Checkbox
                      checked={selectedBrands.some(b => b.toLowerCase() === brand.toLowerCase())}
                      onChange={() => handleBrandChange(brand)}
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>
                      {brand}
                    </Typography>
                  }
                  sx={{ ml: -1 }}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* ── Ideal For — only rendered when options exist ── */}
        {idealForOptions.length > 0 && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>Ideal For</FormLabel>
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <RadioGroup
                value={selectedIdealFor}
                onChange={(e) => setSelectedIdealFor(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio sx={radioSx} />}
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>All</Typography>}
                  sx={{ ml: -1 }}
                />
                {idealForOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio sx={radioSx} />}
                    label={
                      <Typography sx={{ ...rubikfont, fontSize: "0.9rem", textTransform: "capitalize", color: "#444" }}>
                        {option}
                      </Typography>
                    }
                    sx={{ ml: -1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* ── Skin Type — only rendered when options exist ── */}
        {skinTypes.length > 0 && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>Skin Type</FormLabel>
            <FormGroup sx={{ gap: 0, maxHeight: 200, overflowY: "auto", pr: 1 }}>
              {skinTypes.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedSkinTypes.includes(type)}
                      onChange={() => handleSkinTypeChange(type)}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>{type}</Typography>}
                  sx={{ ml: -1 }}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* ── Hair Type — only rendered when options exist ── */}
        {hairTypes.length > 0 && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>Hair Type</FormLabel>
            <FormGroup sx={{ gap: 0, maxHeight: 200, overflowY: "auto", pr: 1 }}>
              {hairTypes.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedHairTypes.includes(type)}
                      onChange={() => handleHairTypeChange(type)}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>{type}</Typography>}
                  sx={{ ml: -1 }}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* ── Trust Badges — only if at least one badge type exists in listing ── */}
        {showTrustBadges && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>Trust Badges</FormLabel>
            <FormGroup>
              {hasSomeVegan && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isVegan}
                      onChange={(e) => setIsVegan(e.target.checked)}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>Vegan</Typography>}
                  sx={{ ml: -1 }}
                />
              )}
              {hasSomeCrueltyFree && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCrueltyFree}
                      onChange={(e) => setIsCrueltyFree(e.target.checked)}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>Cruelty Free</Typography>}
                  sx={{ ml: -1 }}
                />
              )}
              {hasSomeOrganic && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOrganic}
                      onChange={(e) => setIsOrganic(e.target.checked)}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>Organic</Typography>}
                  sx={{ ml: -1 }}
                />
              )}
            </FormGroup>
          </Box>
        )}

        {/* ── COD Available — only if at least one product in listing offers COD ── */}
        {hasSomeCOD && (
          <Box sx={sectionBox}>
            <FormLabel component="legend" sx={labelStyle}>COD Available</FormLabel>
            <RadioGroup value={codAvailable} onChange={(e) => setCodAvailable(e.target.value)}>
              {["all", "yes", "no"].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio sx={radioSx} />}
                  label={
                    <Typography sx={{ ...rubikfont, fontSize: "0.9rem", textTransform: "capitalize", color: "#444" }}>
                      {option === "all" ? "All" : option}
                    </Typography>
                  }
                  sx={{ ml: -1 }}
                />
              ))}
            </RadioGroup>
          </Box>
        )}
      {/* ── Additional Options ── */}
      <Box sx={sectionBox}>
        <FormLabel component="legend" sx={labelStyle}>Availability</FormLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeOutOfStock}
                onChange={(e) => setIncludeOutOfStock(e.target.checked)}
                sx={checkboxSx}
              />
            }
            label={<Typography sx={{ ...rubikfont, fontSize: "0.9rem", color: "#444" }}>Include Out of Stock</Typography>}
            sx={{ ml: -1 }}
          />
        </FormGroup>
      </Box>

      </Box>

      {/* Footer — Reset button */}
      <Box
        sx={{
          p: variant === "static" ? "20px 0" : 3,
          borderTop: variant === "static" ? "none" : "1px solid",
          borderColor: "divider",
          backgroundColor: variant === "static" ? "transparent" : "background.default",
          position: variant === "static" ? "static" : "sticky",
          bottom: 0,
        }}
      >
        <Button
          variant="text"
          fullWidth
          onClick={onClearFilters}
          sx={{
            ...rubikfont,
            py: 0.5,
            color: "#999",
            fontSize: "0.75rem",
            textDecoration: "underline",
            "&:hover": { backgroundColor: "transparent", color: "#C8A054" },
          }}
        >
          Reset All
        </Button>
      </Box>

    </Box>
  );

  if (variant === "static") {
    return FilterContent;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          boxSizing: "border-box",
        },
      }}
    >
      {FilterContent}
    </Drawer>
  );
};

export default Filter;
